// src/pages/Profile/ProfileController.ts
import {useState, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {User} from '../../types'
import {api, getCurrentUser, logout} from '../../services/api'

export interface ProfileFormData {
    phone_number: string
    email: string
}

export interface PasswordFormData {
    currentPassword: string
    newPassword: string
    confirmPassword: string
}

export const useProfileController = () => {
    const [wallets, setWallets] = useState({alfa: 0, touch: 0})
    const [user, setUser] = useState<User | null>(null)
    const [loading, setLoading] = useState(true)
    const [profileLoading, setProfileLoading] = useState(true)
    const [editing, setEditing] = useState(false)
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [saving, setSaving] = useState(false)
    const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null)

    const [formData, setFormData] = useState<ProfileFormData>({
        phone_number: '',
        email: ''
    })

    const [passwordData, setPasswordData] = useState<PasswordFormData>({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    })

    const [showCurrentPassword, setShowCurrentPassword] = useState(false)
    const [showNewPassword, setShowNewPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [isChangingPassword, setIsChangingPassword] = useState(false)
    const [passwordMessage, setPasswordMessage] = useState<{type: 'success' | 'error', text: string} | null>(null)

    const navigate = useNavigate()

    const fetchWallets = async () => {
        try {
            const response = await api.get('/api/admin/wallets')
            if (response.data.success) {
                setWallets(response.data.data)
            }
        } catch (error) {
            console.error('Error fetching wallets:', error)
        }
    }

    useEffect(() => {
        fetchUserData()
        fetchProfileData()
        fetchWallets()
    }, [])

    const fetchUserData = async () => {
        try {
            const currentUser = getCurrentUser()
            setUser(currentUser)
            if (currentUser) {
                setFormData({
                    phone_number: currentUser.phone_number || '',
                    email: currentUser.email || 'user@example.com'
                })
            }
        } catch (error) {
            console.error('Error fetching user:', error)
        } finally {
            setLoading(false)
        }
    }

    const fetchProfileData = async () => {
        setProfileLoading(true)
        try {
            const response = await api.get('/api/user/profile')
            console.log('Profile data:', response.data)

            if (response.data.success && response.data.data) {
                const profileData = response.data.data
                console.log('📞 Phone from API:', profileData.phone_number)

                setUser(prev => {
                    const updatedUser = prev ? {...prev, ...profileData} : profileData
                    console.log('👤 Updated user:', updatedUser)
                    return updatedUser
                })

                setFormData({
                    phone_number: profileData.phone_number || profileData.phone || '',
                    email: profileData.email || ''
                })

                console.log('📞 Phone set to:', profileData.phone_number || profileData.phone || '')
            }
        } catch (error) {
            console.error('Error fetching profile data:', error)
        } finally {
            setProfileLoading(false)
        }
    }


    const toggleSidebar = () => setSidebarOpen(!sidebarOpen)

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target
        setFormData(prev => ({...prev, [name]: value}))
    }

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target
        setPasswordData(prev => ({...prev, [name]: value}))
    }

    const handleSaveProfile = async () => {
        setSaving(true)
        setMessage(null)

        try {
            const response = await api.put('/api/user/profile', {
                phone_number: formData.phone_number,
                email: formData.email
            })

            if (response.data.success) {
                if (user) {
                    const updatedUser = {
                        ...user,
                        phone_number: formData.phone_number,
                        email: formData.email
                    }
                    setUser(updatedUser)
                    localStorage.setItem('user', JSON.stringify(updatedUser))
                }

                setMessage({type: 'success', text: 'تم حفظ التغييرات بنجاح'})
                setEditing(false)
            } else {
                setMessage({type: 'error', text: response.data.error || 'فشل التحديث'})
            }

            setTimeout(() => setMessage(null), 3000)
        } catch (error: any) {
            setMessage({type: 'error', text: error.response?.data?.error || 'حدث خطأ في الاتصال'})
        } finally {
            setSaving(false)
        }
    }

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault()
        setPasswordMessage(null)

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPasswordMessage({type: 'error', text: 'كلمة المرور الجديدة وتأكيدها غير متطابقين'})
            return
        }
        if (passwordData.newPassword.length < 6) {
            setPasswordMessage({type: 'error', text: 'كلمة المرور الجديدة يجب أن تكون 6 أحرف على الأقل'})
            return
        }

        setIsChangingPassword(true)
        try {
            await new Promise(resolve => setTimeout(resolve, 1000))
            setPasswordMessage({type: 'success', text: 'تم تغيير كلمة المرور بنجاح'})
            setPasswordData({currentPassword: '', newPassword: '', confirmPassword: ''})
            setTimeout(() => setPasswordMessage(null), 3000)
        } catch (error) {
            setPasswordMessage({type: 'error', text: 'حدث خطأ في الاتصال'})
        } finally {
            setIsChangingPassword(false)
        }
    }

    const handleLogout = () => {
        logout()
        navigate('/login')
    }

    const getWalletType = (): string => {
        if (!user?.id) return 'غير محدد';
        if (user.role === 'admin' || user.is_admin === 1) {
            return 'System (Alfa & Touch)';
        }
        if (user.id.startsWith('A')) return 'Alfa';
        if (user.id.startsWith('T')) return 'Touch';
        return 'غير محدد'
    }

    return {
        wallets,
        user,
        loading: loading || profileLoading,
        editing,
        sidebarOpen,
        saving,
        message,
        formData,
        passwordData,
        showCurrentPassword,
        showNewPassword,
        showConfirmPassword,
        isChangingPassword,
        passwordMessage,
        setEditing,
        toggleSidebar,
        handleInputChange,
        handlePasswordChange,
        handleSaveProfile,
        handleChangePassword,
        handleLogout,
        setShowCurrentPassword,
        setShowNewPassword,
        setShowConfirmPassword,
        getWalletType
    }
}